import { useState, Fragment, useEffect } from "react";
import { PlayCircle, Lock } from "lucide-react";
import {
  ChevronRight,
  Book,
  Code,
  Zap,
  FileText,
  Settings,
  Shield,
  GitBranch,
  FunctionSquare,
  CheckCircle,
  Terminal,
  Braces,
  ChevronDown,
  BookOpen,
  Menu,
  X
} from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { nodesDocs } from "@/data/nodes-docs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

/* =====================================================
   DADOS DA DOCUMENTAÇÃO (completos e dentro do arquivo)
   ===================================================== */
/* ── MÓDULOS DE VÍDEO ── */
const tutorialModules = [
  {
    id: 1,
    title: "Apresentação do Flow",
    videoSrc: "/videos/modulo-apresentacao.mp4",
    description: "Conheça o AscFlow: uma visão geral da plataforma, suas funcionalidades e como ela pode transformar seus processos.",
    isLocked: false,
  },
  {
    id: 2,
    title: "Criando o Primeiro Flow",
    videoSrc: "/videos/modulo-1.mp4",
    description: "Aprenda como criar seu primeiro fluxo no AscFlow do zero, com um exemplo prático e simples.",
    isLocked: false,
  },
  {
    id: 3,
    title: "Apresentação das Atividades",
    videoSrc: "",
    description: "Explore os tipos de atividades disponíveis e como utilizá-las nos seus fluxos.",
    isLocked: true,
  },
  {
    id: 4,
    title: "Apresentação dos Objetos (Flow, Form, Templates...)",
    videoSrc: "",
    description: "Entenda os principais objetos da plataforma: Flows, Forms, Templates e muito mais.",
    isLocked: true,
  },
  {
    id: 5,
    title: "Básico da Linguagem de Expressão",
    videoSrc: "",
    description: "Aprenda os fundamentos da linguagem de expressão do AscFlow.",
    isLocked: true,
  },
  {
    id: 6,
    title: "Criação do seu Primeiro Fluxo — Exemplo Simples",
    videoSrc: "",
    description: "Passo a passo para criar seu primeiro fluxo funcional do zero.",
    isLocked: true,
  },
];

const documentationData: any = {
  ...nodesDocs,
  "tutoriais": {
    title: "Tutoriais em Vídeo",
    description: "Aprenda a usar o AscFlow com tutoriais em vídeo organizados em módulos.",
    sections: [{ title: "", content: "__TUTORIALS_COMPONENT__" }],
  },
  "visao-geral": {
    title: "Visão Geral da Linguagem AscFlow",
    description:
      "AscFlow é uma linguagem de domínio específico (DSL) projetada para processamento dinâmico de templates e automação de fluxos de trabalho.",
    sections: [
      {
        title: "O que é AscFlow?",
        content: `
          <div class="space-y-4">
            <p>A <strong>AscFlow</strong> utiliza sintaxe com chaves duplas <code>{{ }}</code> para expressões executáveis, permitindo interpolação de dados, execução de funções e aplicação de condições lógicas.</p>
          </div>
        `,
      },
      {
        title: "Casos de Uso",
        content: `
          <div class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-muted/50 p-4 rounded-lg border">
                <h4 class="font-bold text-primary mb-2 flex items-center gap-2">
                  Automação de Processos
                </h4>
                <p class="text-sm text-muted-foreground">Fluxos que tomam decisões baseadas em regras de negócio e executam ações automáticas.</p>
              </div>
              <div class="bg-muted/50 p-4 rounded-lg border">
                <h4 class="font-bold text-primary mb-2 flex items-center gap-2">
                  Geração de Conteúdo
                </h4>
                <p class="text-sm text-muted-foreground">Personalização dinâmica de e-mails, documentos e relatórios com dados do sistema.</p>
              </div>
              <div class="bg-muted/50 p-4 rounded-lg border">
                <h4 class="font-bold text-primary mb-2 flex items-center gap-2">
                  Integração de Sistemas
                </h4>
                <p class="text-sm text-muted-foreground">Transformação de dados entre diferentes APIs e formatos (JSON, XML, CSV).</p>
              </div>
              <div class="bg-muted/50 p-4 rounded-lg border">
                <h4 class="font-bold text-primary mb-2 flex items-center gap-2">
                  Relatórios Dinâmicos
                </h4>
                <p class="text-sm text-muted-foreground">Cálculo de métricas em tempo real para dashboards e visualizações.</p>
              </div>
            </div>
          </div>
        `
      },
      {
        title: "Filosofia e Paradigma",
        content: `
          <div class="space-y-6">
            <p>AscFlow segue uma abordagem <strong>declarativa</strong>: você especifica <em>o que</em> deseja obter, e o interpretador resolve <em>como</em> processar.</p>
            <ul class="space-y-3">
              <li class="flex gap-3 text-sm">
                <span class="bg-primary/10 text-primary w-6 h-6 rounded flex items-center justify-center font-bold shrink-0">1</span>
                <span><strong>No-Code / Low-Code:</strong> Sintaxe intuitiva para usuários sem experiência técnica profunda.</span>
              </li>
              <li class="flex gap-3 text-sm">
                <span class="bg-primary/10 text-primary w-6 h-6 rounded flex items-center justify-center font-bold shrink-0">2</span>
                <span><strong>Avaliação Lazy:</strong> Expressões são processadas apenas quando necessário, otimizando performance.</span>
              </li>
              <li class="flex gap-3 text-sm">
                <span class="bg-primary/10 text-primary w-6 h-6 rounded flex items-center justify-center font-bold shrink-0">3</span>
                <span><strong>Composibilidade:</strong> Combine funções e operadores como blocos de construção.</span>
              </li>
            </ul>
          </div>
        `
      }
    ],
  },

  "quick-start": {
    title: "Quick Start",
    description: "Aprenda a criar seu primeiro fluxo AscFlow em poucos minutos.",
    sections: [
      {
        title: "1. Acesso à Plataforma",
        content: `
          <div class="space-y-4">
            <p>Acesse o painel em <a href="https://flow.ascconecta.com.br" class="text-primary underline">flow.ascconecta.com.br</a>. No **Editor Visual**, você construirá fluxos usando blocos de arraste e solte.</p>
            <div class="bg-primary/5 p-4 rounded-md border border-primary/10">
              <p class="text-sm font-bold flex items-center gap-2">
                <span class="text-primary">Dica:</span>
              </p>
              <p class="text-xs text-muted-foreground">Comece importando um template pronto da biblioteca para entender a estrutura.</p>
            </div>
          </div>
        `
      },
      {
        title: "2. Criando o Primeiro Flow",
        content: `
          <div class="space-y-4">
            <p>Um fluxo é formado por <strong>nodes</strong> (ações) e <strong>conexões</strong> (caminhos). Use chaves duplas para inserir dados dinâmicos em qualquer campo:</p>
            <pre class="bg-muted p-2 rounded text-sm font-mono mt-1"><code>Olá {{ json(get(node-1)).nome }}, seu ticket #{{ json(get(node-1)).id }} foi aberto!</code></pre>
          </div>
        `
      }
    ],
  },

  "estrutura": {
    title: "Estrutura de um Flow",
    description: "Compreenda a mecânica por trás das expressões e do contexto.",
    sections: [
      {
        title: "O Contexto de Execução",
        content: `
          <div class="space-y-6">
            <p>O <strong>Contexto</strong> é o coração do AscFlow. É uma lista de todos os dados produzidos pelos nodes anteriores, organizados por chaves (keys).</p>
            
            <div class="bg-muted p-4 rounded-lg border">
              <h4 class="text-sm font-bold text-primary mb-2 uppercase tracking-tight">Estrutura Interna do Contexto</h4>
              <pre class="text-xs font-mono"><code>[
  { "key": "webhook", "value": { "lead": "João", "score": 90 } },
  { "key": "sql", "value": { "saldo": 150.50 } }
]</code></pre>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="p-4 rounded-lg border bg-card">
                <h5 class="font-bold text-sm mb-1 text-primary">Sintaxe Padrão</h5>
                <p class="text-xs text-muted-foreground"><code>{{ expressao }}</code></p>
              </div>
              <div class="p-4 rounded-lg border bg-card">
                <h5 class="font-bold text-sm mb-1 text-primary">Acesso a Dados</h5>
                <p class="text-xs text-muted-foreground"><code>get(node-1)</code></p>
              </div>
            </div>
          </div>
        `
      }
    ],
  },

  "nodes": {
    title: "Tipos de Node e Comportamentos",
    description:
      "Os nodes representam as unidades fundamentais de execução em um fluxo AscFlow. Cada tipo de node possui um papel específico dentro do fluxo.",
    sections: [
      {
        title: "Conceito de Nodes no AscFlow",
        content: ` 
        Embora o interpretador AscFlow em si seja agnóstico quanto aos tipos específicos de nodes, a linguagem foi projetada para trabalhar em um ambiente onde diferentes tipos de nodes processam dados e disponibilizam resultados para nodes subsequentes. Cada node representa uma unidade de processamento que pode executar operações específicas como requisições HTTP, transformações de dados, condições lógicas, loops, delays, entre outros. 
        
        O interpretador AscFlow atua como a camada de expressão que permite que esses nodes se comuniquem e compartilhem dados de forma dinâmica. Quando um node precisa acessar dados de nodes anteriores ou executar cálculos baseados em contexto, ele utiliza expressões AscFlow para definir essa lógica. 
        
      <strong style="font-size: 1.2rem;">Tipos Padrão de Nodes </strong>

      Baseado na análise do interpretador e nas funções disponíveis, podemos identificar os seguintes tipos de nodes que são comumente suportados:

      <strong style="font-size: 1.2rem;">HTTP Request Nodes: </strong>
      Estes nodes executam requisições HTTP para APIs externas e disponibilizam as respostas para outros nodes. O interpretador AscFlow permite que esses nodes construam URLs dinâmicas, headers personalizados e payloads baseados em dados de contexto. Por exemplo, uma URL pode ser construída como <code style="color:#008000">https://api.exemplo.com/users/{{ get(node-5) }} </code>, onde o ID do usuário vem de um node anterior.
        
      <strong style="font-size: 1.2rem;">Conditional Nodes (If): </strong>
        Nodes condicionais avaliam expressões booleanas e direcionam o fluxo de execução baseado no resultado. O interpretador suporta condições complexas que podem comparar valores, verificar existência de propriedades e combinar múltiplas condições usando operadores lógicos. A sintaxe <code style ="color: #008000"> if(condição) { valor_verdadeiro } else { valor_falso } </code> permite que esses nodes tomem decisões sofisticadas.

       <strong style="font-size: 1.2rem;">Loop Nodes: </strong>
       Nodes de loop processam arrays de dados, executando operações para cada item. O interpretador AscFlow fornece funções como <code style="color: #008000"> at(), length() e last() </code> que facilitam o trabalho com arrays dentro de loops. Esses nodes podem iterar sobre dados de APIs, processar listas de clientes ou executar operações em lote. 


       <strong style="font-size: 1.2rem;">Data Transformation Nodes: </strong>
       Estes nodes manipulam e transformam dados usando as funções embutidas do AscFlow. Eles podem converter formatos de data, extrair informações específicas de strings, realizar cálculos matemáticos e reformatar dados para diferentes sistemas. 

        <strong style="font-size: 1.2rem;">Delay Nodes: </strong>
        Nodes de delay introduzem pausas no fluxo de execução. Embora o interpretador não gerencie diretamente os delays, ele pode calcular dinamicamente os tempos de espera baseado em condições ou dados do contexto. 

<strong style="font-size: 1.2rem;">Variable Assignment Nodes: </strong> 
Estes nodes criam ou atualizam variáveis que ficam disponíveis para nodes subsequentes. O interpretador permite que esses nodes calculem valores complexos e os armazenem com chaves específicas no contexto. 

<strong style="font-size: 1.2rem;">Switch/Case Nodes: </strong>
Nodes que implementam lógica de switch-case podem utilizar a função <code style ="color:#008000">case() </code> do interpretador para avaliar múltiplas condições e retornar valores apropriados baseados em diferentes cenários. 
        `

      },
      {
        title: "Campos Obrigatórios e Opcionais",
        content: `
          Cada tipo de node possui campos específicos que definem seu comportamento. O interpretador AscFlow é utilizado para processar esses campos e torná-los dinâmicos.

          <strong style="font-size: 1.2rem;">Campos de Identificação: </strong> Todo node deve ter um identificador único (key) que permite que outros nodes referenciem seus dados através da função get(). Este campo é obrigatório e deve ser único dentro do contexto do fluxo. 

          <strong style="font-size: 1.2rem;">Campos de Configuração: </strong> Dependendo do tipo de node, diferentes campos de configuração são necessários. Por exemplo, HTTP Request nodes requerem URL, método HTTP e opcionalmente headers e body. Estes campos podem conter expressões AscFlow que são avaliadas em tempo de execução. 

          <strong style="font-size: 1.2rem;">Campos de Output: </strong> Nodes podem definir como seus resultados são estruturados e quais propriedades ficam disponíveis para nodes subsequentes. O interpretador permite que esses campos sejam calculados dinamicamente baseado nos dados processados. 

         <strong style="font-size: 1.2rem;">Campos de Tratamento de Erro: </strong> Nodes podem definir comportamentos específicos para situações de erro, incluindo valores padrão, tentativas de retry e ações de fallback. Estes comportamentos podem ser condicionais, utilizando expressões AscFlow para determinar a ação apropriada.

          `
      },

      {
        title: "Comportamentos Específicos de Execução",
        content: `
        <strong style="font-size: 1.1rem;"><code style ="color:#d3d3d3"> O interpretador AscFlow influencia significativamente o comportamento de execução dos nodes através de várias características: </strong> </code> 

        <strong style="font-size: 1.2rem;">Avaliação Lazy: </strong> Expressões são avaliadas apenas quando necessário, permitindo que nodes otimizem sua execução. Se uma condição em um node condicional é falsa, as expressões no branch verdadeiro não são avaliadas, economizando recursos computacionais.

        <strong style="font-size: 1.2rem;">Resolução de Dependências: </strong> Quando um node referencia dados de outros nodes através da função get(), o interpretador automaticamente resolve essas dependências. A função getLastOccurrence() garante que sempre a versão mais recente dos dados seja utilizada, mesmo se múltiplos nodes tiverem a mesma chave. 

        <strong style="font-size: 1.2rem;">Conversão Automática de Tipos: </strong> O interpretador realiza conversões automáticas entre tipos de dados, simplificando a integração entre nodes que podem produzir dados em formatos diferentes. Strings numéricas são automaticamente convertidas para números em operações matemáticas, e objetos JSON são parseados transparentemente. 

        <strong style="font-size: 1.2rem;">Tratamento de Valores Indefinidos: </strong> Quando um node tenta acessar dados que não existem, o interpretador retorna valores padrão apropriados (strings vazias, undefined) ao invés de gerar erros, permitindo que o fluxo continue executando mesmo com dados incompletos. 

        <strong style="font-size: 1.2rem;">Composição de Expressões: </strong> Nodes podem utilizar expressões complexas que combinam múltiplas funções e operadores. Por exemplo, um node pode calcular um desconto usando <code style ="color:#008000">{{ json(get(node-1)).preco * (json(get(node-2)).categoria == "premium" ? 0.1 : 0.05) }} </code>, ou processar dados aninhados complexos como <code style ="color:#008000">{{ tojson(json(get(node-6).data.Data)) }}</code>. 

        <strong style="font-size: 1.2rem;">Contexto Imutável: </strong> Durante a execução de um node, o contexto permanece imutável, garantindo que todas as expressões dentro do node vejam uma visão consistente dos dados. Isso previne condições de corrida e comportamentos inesperados em fluxos complexos. A flexibilidade do interpretador AscFlow permite que novos tipos de nodes sejam facilmente adicionados ao sistema sem modificações no core da linguagem. Qualquer node que precise processar dados dinamicamente pode utilizar expressões AscFlow para definir sua lógica, mantendo a consistência e simplicidade da plataforma. 

        `
      }

    ],
  },

  "variaveis": {
    title: "Variáveis e Contextos",
    description: "Entenda como o AscFlow gerencia o estado e o fluxo de dados entre os nodes.",
    sections: [
      {
        title: "O Coração do Fluxo: O Contexto",
        content: `
          <div class="space-y-6">
            <p>O Contexto é um registro cronológico de tudo o que acontece durante a execução. Cada node, ao finalizar sua tarefa, "carimba" seus resultados no contexto usando uma <strong>key</strong> única.</p>
            
            <div class="bg-card border rounded-lg p-6 shadow-sm">
              <h4 class="text-sm font-bold text-primary mb-4 uppercase tracking-wider">Anatomia de uma Variável</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="space-y-2">
                  <div class="text-xs font-bold text-muted-foreground uppercase">Chave (Key)</div>
                  <div class="p-3 bg-muted rounded font-mono text-sm leading-none text-primary">api_usuario</div>
                  <p class="text-xs text-muted-foreground italic">O identificador único para busca.</p>
                </div>
                <div class="space-y-2">
                  <div class="text-xs font-bold text-muted-foreground uppercase">Valor (Value)</div>
                  <div class="p-3 bg-muted rounded font-mono text-sm leading-none text-primary">{ "id": 1, "nome": "Ana" }</div>
                  <p class="text-xs text-muted-foreground italic">O dado real retornado pelo node.</p>
                </div>
              </div>
            </div>
          </div>
        `
      },
      {
        title: "Tipos de Variáveis",
        content: `
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="p-5 rounded-xl border bg-card hover:shadow-md transition-shadow">
              <h4 class="font-bold text-primary mb-2">Globais</h4>
              <p class="text-sm text-muted-foreground">Configurações e credenciais que persistem durante toda a vida do fluxo.</p>
            </div>
            <div class="p-5 rounded-xl border bg-card hover:shadow-md transition-shadow">
              <h4 class="font-bold text-primary mb-2">Entrada (Input)</h4>
              <p class="text-sm text-muted-foreground">Dados brutos que iniciam o fluxo (JSON de Webhooks, parâmetros de Trigger).</p>
            </div>
            <div class="p-5 rounded-xl border bg-card hover:shadow-md transition-shadow">
              <h4 class="font-bold text-primary mb-2">Saída (Output)</h4>
              <p class="text-sm text-muted-foreground">O resultado final processado que será enviado para sistemas externos.</p>
            </div>
            <div class="p-5 rounded-xl border bg-card hover:shadow-md transition-shadow">
              <h4 class="font-bold text-primary mb-2">Internas</h4>
              <p class="text-sm text-muted-foreground">Cálculos intermediários e estados temporários criados por nodes de lógica.</p>
            </div>
          </div>
        `
      },
      {
        title: "Resolvendo Conflitos",
        content: `
          <div class="space-y-4">
            <p>Se dois nodes usarem a mesma chave, o AscFlow mantém o histórico, mas a função <code>get()</code> sempre prioriza a <strong>última ocorrência</strong>.</p>
            <div class="bg-primary/5 border border-primary/20 p-4 rounded-lg">
              <p class="text-sm font-bold text-primary">Regra de Ouro</p>
              <p class="text-sm">Isso permite o padrão de "Refinamento": você pode ter vários nodes atualizando a chave <code>status</code> e o sistema sempre usará o valor mais recente no momento da leitura.</p>
            </div>
          </div>
        `
      }
    ],
  },

  "condicoes": {
    title: "Condições e Lógica",
    description: "Crie fluxos inteligentes que tomam decisões baseadas em dados em tempo real.",
    sections: [
      {
        title: "A Estrutura If/Else",
        content: `
          <div class="space-y-4">
            <p>A lógica condicional do AscFlow permite direcionar o fluxo de dados conforme o resultado de uma expressão booleana.</p>
            <div class="bg-primary/5 border border-primary/20 p-4 rounded-md font-mono text-sm text-primary">
              {{ if(condicao) { "valor se verdadeiro" } else { "valor se falso" } }}
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div class="p-4 rounded-lg border bg-card">
                <h5 class="font-bold text-sm text-primary mb-1">Operadores de Comparação</h5>
                <p class="text-xs text-muted-foreground"><code>==</code>, <code>!=</code>, <code>&lt;</code>, <code>&gt;</code>, <code>&lt;=</code>, <code>&gt;=</code></p>
              </div>
              <div class="p-4 rounded-lg border bg-card">
                <h5 class="font-bold text-sm text-primary mb-1">Operadores Lógicos</h5>
                <p class="text-xs text-muted-foreground"><code>&&</code> (E), Próximas atualizações para <code>||</code> (OU).</p>
              </div>
            </div>
          </div>
        `
      },
      {
        title: "Triade Essencial: get, json, tojson",
        content: `
          <div class="space-y-6">
            <div class="flex flex-col gap-4">
              <div class="flex gap-4 p-4 rounded-xl bg-muted/30 border animate-in slide-in-from-left duration-500">
                <div class="bg-primary text-white w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-bold">1</div>
                <div>
                  <h4 class="font-bold text-primary">get(node-id)</h4>
                  <p class="text-sm text-muted-foreground">O <strong>get</strong> é a ferramenta de busca do AscFlow. Ele serve para encontrar e trazer para o seu node atual os dados que foram gerados em qualquer passo anterior do fluxo. Sem o get, os blocos seriam isolados e não conseguiriam compartilhar informações.</p>
                  <p class="text-xs mt-2 text-primary/80 font-mono italic">Exemplo: get(node-1) busca tudo o que o primeiro node entregou.</p>
                </div>
              </div>

              <div class="flex gap-4 p-4 rounded-xl bg-muted/30 border animate-in slide-in-from-left duration-700">
                <div class="bg-primary text-white w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-bold">2</div>
                <div>
                  <h4 class="font-bold text-primary">json(dados)</h4>
                  <p class="text-sm text-muted-foreground">O <strong>json</strong> é o tradutor. Muitas vezes, um node (como uma busca em Banco ou API) retorna apenas um bloco de texto bruto. A função json() transforma esse texto em um objeto organizado, permitindo que você acesse propriedades específicas via ponto (ex: ".id" ou ".email").</p>
                  <p class="text-xs mt-2 text-primary/80 font-mono italic">Exemplo: json(get(node-1)).nome extrai apenas o nome da resposta.</p>
                </div>
              </div>

              <div class="flex gap-4 p-4 rounded-xl bg-muted/30 border animate-in slide-in-from-left duration-1000">
                <div class="bg-primary text-white w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-bold">3</div>
                <div>
                  <h4 class="font-bold text-primary">tojson(valor)</h4>
                  <p class="text-sm text-muted-foreground">O <strong>tojson</strong> é o empacotador. Ele faz o inverso do json(): pega um dado organizado e o transforma novamente em uma string de texto. Isso é essencial quando você precisa enviar dados para uma API externa ou salvar um objeto complexo em um campo de texto no banco.</p>
                  <p class="text-xs mt-2 text-primary/80 font-mono italic">Exemplo: tojson(get(node-1)) prepara o objeto para ser enviado em um payload.</p>
                </div>
              </div>
            </div>
          </div>
        `
      }
    ],
  },


  "interpolacao": {
    title: "Interpolação e Formatação",
    description: "Técnicas para transformar dados brutos em mensagens ricas e informativas.",
    sections: [
      {
        title: "Poder de Expressão",
        content: `
          <div class="space-y-6">
            <p>O interpretador AscFlow permite combinar texto estático com múltiplas lógicas em um único template.</p>
            
            <div class="grid grid-cols-1 gap-4">
              <div class="bg-card border rounded-lg overflow-hidden shrink-0">
                <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b flex justify-between items-center">
                  <span>Manipulação de Texto</span>
                  <span class="text-[10px] bg-primary/10 text-primary px-2 rounded-full">Exemplo</span>
                </div>
                <div class="p-4 font-mono text-sm leading-relaxed text-foreground">
                  Olá <span class="text-primary">{{ json(get(node-1)).nome }}</span><br/>
                  Documento: <span class="text-primary">{{ left(json(get(node-2)).numero, 3) }}</span>-<span class="text-primary">{{ right(json(get(node-2)).numero, 4) }}</span>
                </div>
              </div>

              <div class="bg-card border rounded-lg overflow-hidden shrink-0">
                <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b flex justify-between items-center">
                  <span>Cálculos Aritméticos</span>
                  <span class="text-[10px] bg-primary/10 text-primary px-2 rounded-full">Exemplo</span>
                </div>
                <div class="p-4 font-mono text-sm leading-relaxed text-foreground">
                  Total com Desconto: R$ <span class="text-primary">{{ json(get(node-3)).preco * (1 - json(get(node-4))) }}</span>
                </div>
              </div>

              <div class="bg-card border rounded-lg overflow-hidden shrink-0">
                <div class="bg-muted px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b flex justify-between items-center">
                  <span>Listas e Arrays</span>
                  <span class="text-[10px] bg-primary/10 text-primary px-2 rounded-full">Exemplo</span>
                </div>
                <div class="p-4 font-mono text-sm leading-relaxed text-foreground">
                  Primeiro item: <span class="text-primary">{{ at(json(get(node-1)), 0).nome }}</span><br/>
                  Total de itens: <span class="text-primary">{{ length(json(get(node-1))) }}</span>
                </div>
              </div>
            </div>
          </div>
        `
      }
    ],
  },

  "funcoes": {
    title: "Biblioteca de Funções",
    description: "Catálogo completo de todas as funções embutidas disponíveis na linguagem AscFlow, organizadas por categoria.",
    sections: [
      {
        title: "Acesso a Dados",
        content: `
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">get(chave)</h4>
              <p class="text-xs text-muted-foreground mb-2">Busca valor no contexto pelo identificador do node.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ get("usuario_id") }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">json(string)</h4>
              <p class="text-xs text-muted-foreground mb-2">Converte JSON string para objeto acessível.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ json(get("dados")).nome }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">tojson(valor)</h4>
              <p class="text-xs text-muted-foreground mb-2">Converte valor para JSON string.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ tojson(objeto) }}</span></code>
            </div>
          </div>
        `
      },
      {
        title: "Manipulação de Texto (Strings)",
        content: `
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">replace(str, busca, substitui)</h4>
              <p class="text-xs text-muted-foreground mb-2">Substitui ocorrências de texto em uma string.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ replace("João Silva", "Silva", "Santos") }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">split(str, delim)</h4>
              <p class="text-xs text-muted-foreground mb-2">Divide string em array usando delimitador.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ split("a,b,c", ",") }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">charindex(substr, str, [start])</h4>
              <p class="text-xs text-muted-foreground mb-2">Encontra posição de uma substring dentro de uma string.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ charindex("@", email) }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">extractEmail(str)</h4>
              <p class="text-xs text-muted-foreground mb-2">Extrai o primeiro email encontrado em um texto.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ extractEmail("Contato: joao@email.com") }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">left(str, length)</h4>
              <p class="text-xs text-muted-foreground mb-2">Retorna os primeiros N caracteres de uma string.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ left("AscFlow", 3) }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">right(str, length)</h4>
              <p class="text-xs text-muted-foreground mb-2">Retorna os últimos N caracteres de uma string.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ right("AscFlow", 4) }}</span></code>
            </div>
          </div>
        `
      },
      {
        title: "Conversão de Tipos",
        content: `
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">cast(valor, tipo)</h4>
              <p class="text-xs text-muted-foreground mb-2">Converte valor para o tipo especificado (ex: "number", "string").</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ cast("123", "number") }}</span></code>
            </div>
          </div>
        `
      },
      {
        title: "Data e Tempo",
        content: `
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">agora()</h4>
              <p class="text-xs text-muted-foreground mb-2">Retorna data e hora atual em formato ISO.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ agora() }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">dateadd(data, valor, unidade)</h4>
              <p class="text-xs text-muted-foreground mb-2">Adiciona tempo a uma data (day, month, year).</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ dateadd("2025-01-01", 7, "day") }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">datesub(data, valor, unidade)</h4>
              <p class="text-xs text-muted-foreground mb-2">Subtrai tempo de uma data.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ datesub(agora(), 1, "month") }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">hora(data)</h4>
              <p class="text-xs text-muted-foreground mb-2">Extrai a hora de uma data.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ hora(agora()) }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">minute(data)</h4>
              <p class="text-xs text-muted-foreground mb-2">Extrai o minuto de uma data.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ minute(agora()) }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">second(data)</h4>
              <p class="text-xs text-muted-foreground mb-2">Extrai o segundo de uma data.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ second(agora()) }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">day(data)</h4>
              <p class="text-xs text-muted-foreground mb-2">Extrai o dia de uma data.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ day(agora()) }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">month(data)</h4>
              <p class="text-xs text-muted-foreground mb-2">Extrai o mês de uma data.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ month(agora()) }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">year(data)</h4>
              <p class="text-xs text-muted-foreground mb-2">Extrai o ano de uma data.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ year(agora()) }}</span></code>
            </div>
          </div>
        `
      },
      {
        title: "Arrays (Listas)",
        content: `
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">at(array, indice)</h4>
              <p class="text-xs text-muted-foreground mb-2">Acessa elemento por índice em um array.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ at(lista, 0) }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">last(array)</h4>
              <p class="text-xs text-muted-foreground mb-2">Retorna o último elemento de um array.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ last(lista) }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">length(array)</h4>
              <p class="text-xs text-muted-foreground mb-2">Retorna o tamanho (contagem de itens) do array.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ length(lista) }}</span></code>
            </div>
          </div>
        `
      },
      {
        title: "Controle de Fluxo",
        content: `
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">case(cond1, val1, ..., else)</h4>
              <p class="text-xs text-muted-foreground mb-2">Avalia múltiplas condições em sequência (switch/case). Retorna o valor da primeira condição verdadeira, ou o valor else.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ case(status == "ok", "Sucesso", "Erro") }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors">
              <h4 class="font-bold text-primary mb-1">if(condição) { verdadeiro } else { falso }</h4>
              <p class="text-xs text-muted-foreground mb-2">Estrutura condicional para tomar decisões dentro de expressões.</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto"><span class="text-primary">{{ if(idade >= 18) { "Adulto" } else { "Menor" } }}</span></code>
            </div>
          </div>
        `
      },
      {
        title: "Operadores Suportados",
        content: `
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="p-4 rounded-lg border bg-card">
              <h4 class="font-bold text-primary mb-2">Comparação</h4>
              <p class="text-sm font-mono text-muted-foreground">==, !=, <, >, <=, >=</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto mt-2"><span class="text-primary">{{ idade >= 18 }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card">
              <h4 class="font-bold text-primary mb-2">Lógico</h4>
              <p class="text-sm font-mono text-muted-foreground">&&</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto mt-2"><span class="text-primary">{{ idade >= 18 && documento != "" }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card">
              <h4 class="font-bold text-primary mb-2">Aritmético</h4>
              <p class="text-sm font-mono text-muted-foreground">+, -, *, /</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto mt-2"><span class="text-primary">{{ preco * quantidade }}</span></code>
            </div>
            <div class="p-4 rounded-lg border bg-card">
              <h4 class="font-bold text-primary mb-2">Acesso a Propriedades</h4>
              <p class="text-sm font-mono text-muted-foreground">. (ponto) e [] (colchetes)</p>
              <code class="text-[10px] bg-muted p-1 rounded block overflow-x-auto mt-2"><span class="text-primary">{{ objeto.propriedade }}, {{ objeto["chave"] }}</span></code>
            </div>
          </div>
        `
      }
    ],
  },


  "validacoes": {
    title: "Tratamento de Erros",
    description: "Mecanismos para garantir que seus fluxos nunca parem, mesmo diante de imprevistos.",
    sections: [
      {
        title: "A Filosofia do Fail-Safe",
        content: `
          <div class="space-y-6">
            <p>AscFlow prioriza a continuidade. Em vez de interromper o fluxo, o sistema tenta resolver conflitos de forma graciosa.</p>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="p-4 rounded-lg bg-primary/5 border border-primary/20">
                <h4 class="font-bold text-primary mb-1 flex items-center gap-2">
                  Erros Críticos
                </h4>
                <p class="text-xs text-muted-foreground">Sintaxe malformada (chaves não fechadas) interrompem a execução para evitar dados corrompidos.</p>
              </div>
              <div class="p-4 rounded-lg bg-primary/5 border border-primary/20">
                <h4 class="font-bold text-primary mb-1 flex items-center gap-2">
                  Erros de Acesso
                </h4>
                <p class="text-xs text-muted-foreground">Chaves inexistentes retornam uma string vazia ("") em vez de travar o sistema.</p>
              </div>
            </div>

            <div class="bg-primary/5 p-4 rounded-lg border border-primary/20">
              <h4 class="text-sm font-bold text-primary mb-2 flex items-center gap-2">
                Estratégia de Fallback
              </h4>
              <p class="text-sm">Use o padrão de valor padrão para garantir que seu fluxo sempre tenha um dado válido:</p>
              <pre class="bg-muted p-2 rounded text-xs font-mono mt-2"><code>{{ if(get(node-1) != "") { get(node-1) } else { "valor_padrao" } }}</code></pre>
            </div>
          </div>
        `
      }
    ],
  },

  "exemplos": {
    title: "Exemplos Completos",
    description:
      "Veja exemplos reais de aplicação da linguagem AscFlow em automações empresariais e fluxos dinâmicos.",
    sections: [
      {
        title: "Exemplo 1: Fluxo de Processamento de Pedido",
        content: `
          <div class="space-y-6">
            <div>
              <h3 class="text-lg font-bold text-primary mb-2">1. Cenário</h3>
              <p>Um e-commerce precisa processar informações de pedidos e gerar mensagens personalizadas para clientes com base em sua categoria (Premium vs. Normal) e localização, calculando automaticamente taxas e prazos.</p>
            </div>

            <div>
              <h3 class="text-lg font-bold text-primary mb-2">2. Contexto de Execução (Simulação de Nodes)</h3>
              <p class="text-sm text-muted-foreground mb-4">Veja como os dados estão organizados nos nodes anteriores para que as expressões funcionem:</p>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                <div class="p-3 rounded border bg-muted/30 text-[10px] font-mono"><span class="text-primary font-bold">node-1</span>: Dados do Pedido</div>
                <div class="p-3 rounded border bg-muted/30 text-[10px] font-mono"><span class="text-primary font-bold">node-2</span>: Dados do Cliente</div>
                <div class="p-3 rounded border bg-muted/30 text-[10px] font-mono"><span class="text-primary font-bold">node-3</span>: Configurações/Taxas</div>
              </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <!-- Lado Esquerdo: Entrada -->
              <div>
                <div class="flex items-center gap-2 mb-3">
                  <div class="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
                  <h3 class="text-sm font-bold uppercase tracking-wider">Entrada (Template AscFlow)</h3>
                </div>
                <div class="bg-primary/5 border border-primary/20 p-5 rounded-xl font-mono text-[11px] text-foreground leading-loose h-full min-h-[350px] overflow-x-hidden break-words whitespace-pre-wrap">Olá <span class="text-primary">{{ json(get(node-2)).nome }}</span><br/>
Seu pedido #<span class="text-primary">{{ json(get(node-1)).id }}</span> foi confirmado com sucesso.<br/><br/>
                  
Detalhes do Pedido:<br/>
- Valor: R$ <span class="text-primary">{{ json(get(node-1)).total }}</span><br/>
- Taxa de entrega: R$ <span class="text-primary">{{ json(get(node-3)).taxa_entrega }}</span><br/>
- Total final: R$ <span class="text-primary">{{ json(get(node-1)).total + json(get(node-3)).taxa_entrega }}</span><br/><br/>

Prazo de entrega: <span class="text-primary">{{ if(json(get(node-2)).categoria == "premium") { json(get(node-3)).prazo_entrega_premium } else { json(get(node-3)).prazo_entrega_normal } }}</span> dias úteis<br/>
Data prevista: <span class="text-primary">{{ dateadd(json(get(node-1)).data_pedido, if(json(get(node-2)).categoria == "premium") { json(get(node-3)).prazo_entrega_premium } else { json(get(node-3)).prazo_entrega_normal }, "day") }}</span><br/><br/>

Entrega em: <span class="text-primary">{{ json(get(node-2)).endereco.cidade }}</span>, <span class="text-primary">{{ json(get(node-2)).endereco.estado }}</span>
                </div>
              </div>

              <div>
                <div class="flex items-center gap-2 mb-3">
                  <div class="w-2 h-2 rounded-full bg-primary/60"></div>
                  <h3 class="text-sm font-bold uppercase tracking-wider text-primary-foreground/70">Saída (Resultado Final)</h3>
                </div>
                <div class="bg-primary/5 border border-primary/20 p-5 rounded-xl font-mono text-[11px] text-foreground leading-relaxed whitespace-pre-wrap h-full min-h-[350px]">Olá Maria Silva
Seu pedido #PED-2025-001 foi confirmado com sucesso.

Detalhes do Pedido:
- Valor: R$ 299.9
- Taxa de entrega: R$ 15
- Total final: R$ 314.9

Prazo de entrega: 2 dias úteis
Data prevista: 2025-01-26T10:30:00.000Z

Entrega em: São Paulo, SP</div>
              </div>
            </div>

            <div>
              <h3 class="text-lg font-bold text-primary mb-2">5. Explicação do Processamento</h3>
              <ul class="space-y-3">
                <li class="flex gap-2 text-sm">
                  <span class="font-bold text-primary min-w-[20px]">1.</span>
                  <span><strong>Acesso Profundo:</strong> O sistema usa <code>json(get(node-2)).nome</code> para navegar em objetos aninhados.</span>
                </li>
                <li class="flex gap-2 text-sm">
                  <span class="font-bold text-primary min-w-[20px]">2.</span>
                  <span><strong>Cálculo Dinâmico:</strong> A soma do total + taxa ocorre em tempo real, sem necessidade de nodes extras de cálculo.</span>
                </li>
                <li class="flex gap-2 text-sm">
                  <span class="font-bold text-primary min-w-[20px]">3.</span>
                  <span><strong>Inteligência de Data:</strong> O <code>dateadd</code> utiliza o resultado de uma condição <code>if()</code> interna para definir quantos dias somar.</span>
                </li>
              </ul>
            </div>
          </div>
        `
      },
      {
        title: "Exemplo 2: Sistema de Aprovação de Recursos (RH)",
        content: `
          <div class="space-y-6">
            <div>
              <h3 class="text-lg font-bold text-primary mb-2">1. Cenário</h3>
              <p>Um fluxo de RH que valida automaticamente solicitações de férias, cruzando dados do funcionário com as políticas da empresa, verificando elegibilidade por tempo de casa, saldo e antecedência mínima.</p>
            </div>

            <div>
              <h3 class="text-lg font-bold text-primary mb-2">2. Contexto de Execução</h3>
              <div class="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                <div class="p-3 rounded border bg-muted/30 text-[10px] font-mono"><span class="text-primary font-bold">node-1</span>: Solicitação</div>
                <div class="p-3 rounded border bg-muted/30 text-[10px] font-mono"><span class="text-primary font-bold">node-2</span>: Funcionário</div>
                <div class="p-3 rounded border bg-muted/30 text-[10px] font-mono"><span class="text-primary font-bold">node-3</span>: Políticas RH</div>
                <div class="p-3 rounded border bg-muted/30 text-[10px] font-mono"><span class="text-primary font-bold">node-4</span>: Sistema</div>
              </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <!-- Entrada -->
              <div>
                <div class="flex items-center gap-2 mb-3">
                  <div class="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
                  <h3 class="text-sm font-bold uppercase tracking-wider">Entrada (Lógica RH)</h3>
                </div>
                <div class="bg-primary/5 border border-primary/20 p-5 rounded-xl font-mono text-[10px] text-foreground leading-relaxed whitespace-pre-wrap break-words h-full min-h-[450px] overflow-x-hidden">=== ANÁLISE DE SOLICITAÇÃO DE FÉRIAS ===

Funcionário: <span class="text-primary">{{ json(get(node-2)).nome }}</span> (<span class="text-primary">{{ json(get(node-2)).cargo }}</span>)
Período solicitado: <span class="text-primary">{{ json(get(node-1)).data_inicio }}</span> a <span class="text-primary">{{ json(get(node-1)).data_fim }}</span>

--- VERIFICAÇÕES DE ELEGIBILIDADE ---

1. Tempo de casa: <span class="text-primary">{{ if(dateadd(json(get(node-2)).data_admissao, json(get(node-3)).minimo_dias_trabalho, "day") <= json(get(node-4)).data_atual) { "Elegível (mais de 1 ano)" } else { "Não elegível" } }}</span>

2. Saldo disponível: <span class="text-primary">{{ json(get(node-2)).saldo_ferias - json(get(node-2)).ferias_utilizadas }}</span> dias
Status: <span class="text-primary">{{ if(json(get(node-2)).saldo_ferias - json(get(node-2)).ferias_utilizadas >= 10) { "Saldo suficiente" } else { "Saldo baixo" } }}</span>

3. Departamento crítico: <span class="text-primary">{{ if(charindex(json(get(node-2)).departamento, tojson(json(get(node-3)).departamentos_criticos)) >= 0) { "Requer aprovação especial" } else { "Departamento normal" } }}</span>

--- DECISÃO AUTOMÁTICA ---

<span class="text-primary">{{ case(
    dateadd(json(get(node-2)).data_admissao, json(get(node-3)).minimo_dias_trabalho, "day") > json(get(node-4)).data_atual, "REJEITADO: Funcionário sem tempo de casa",
    json(get(node-2)).saldo_ferias - json(get(node-2)).ferias_utilizadas < 10, "REJEITADO: Saldo insuficiente",
    charindex(json(get(node-2)).departamento, tojson(json(get(node-3)).departamentos_criticos)) >= 0, "PENDENTE: Requer aprovação do supervisor " + json(get(node-2)).supervisor,
    "APROVADO: Solicitação atende aos critérios"
) }}</span></div>
              </div>

              <!-- Saída -->
              <div>
                <div class="flex items-center gap-2 mb-3">
                  <div class="w-2 h-2 rounded-full bg-primary/60"></div>
                  <h3 class="text-sm font-bold uppercase tracking-wider text-primary-foreground/70">Saída (Veredito)</h3>
                </div>
                <div class="bg-primary/5 border border-primary/20 p-5 rounded-xl font-mono text-[10px] text-foreground leading-relaxed whitespace-pre-wrap h-full min-h-[450px]">=== ANÁLISE DE SOLICITAÇÃO DE FÉRIAS ===

Funcionário: João Santos (Analista Senior)
Período solicitado: 2025-03-15 a 2025-03-25

--- VERIFICAÇÕES DE ELEGIBILIDADE ---

1. Tempo de casa: Elegível (mais de 1 ano)
2. Saldo disponível: 20 dias
Status: Saldo suficiente
3. Departamento crítico: Requer aprovação especial

--- DECISÃO AUTOMÁTICA ---
PENDENTE: Requer aprovação do supervisor Ana Costa</div>
              </div>
            </div>

            <div>
              <h3 class="text-lg font-bold text-primary mb-2">5. Recursos Avançados Utilizados</h3>
              <ul class="space-y-3">
                <li class="flex gap-2 text-sm">
                  <span class="font-bold text-primary min-w-[20px]">1.</span>
                  <span><strong>Lógica de Switch (case):</strong> Avalia múltiplas condições em cascata para chegar a um veredito final (Aprovado/Rejeitado/Pendente).</span>
                </li>
                <li class="flex gap-2 text-sm">
                  <span class="font-bold text-primary min-w-[20px]">2.</span>
                  <span><strong>Busca em Listas:</strong> O uso de <code>charindex</code> combinado com <code>tojson</code> permite verificar se um valor pertence a uma lista dinâmica (ex: departamentos críticos).</span>
                </li>
                <li class="flex gap-2 text-sm">
                  <span class="font-bold text-primary min-w-[20px]">3.</span>
                  <span><strong>Expressões Aninhadas:</strong> Combinação de cálculos aritméticos (subtração de saldo) com verificações booleanas dentro de um mesmo template.</span>
                </li>
              </ul>
            </div>
          </div>
        `
      },
      {
        title: "Exemplo 3: Fluxo Avançado - Sistema de Notificações",
        content: `
          <div class="space-y-6">
            <div>
              <h3 class="text-lg font-bold text-primary mb-2">1. Cenário</h3>
              <p>Um sistema de monitoramento de infraestrutura processa alertas técnicos, classifica a severidade em tempo real, consulta escalas de plantão e gera recomendações de ação imediata para a equipe de SRE.</p>
            </div>

            <div>
              <h3 class="text-lg font-bold text-primary mb-2">2. Contexto de Execução</h3>
              <div class="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                <div class="p-3 rounded border bg-muted/30 text-[10px] font-mono"><span class="text-primary font-bold">node-1</span>: Alerta Técnico</div>
                <div class="p-3 rounded border bg-muted/30 text-[10px] font-mono"><span class="text-primary font-bold">node-2</span>: Config. Canais</div>
                <div class="p-3 rounded border bg-muted/30 text-[10px] font-mono"><span class="text-primary font-bold">node-3</span>: Histórico Infra</div>
                <div class="p-3 rounded border bg-muted/30 text-[10px] font-mono"><span class="text-primary font-bold">node-4</span>: Escala Equipe</div>
              </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <!-- Entrada -->
              <div>
                <div class="flex items-center gap-2 mb-3">
                  <div class="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
                  <h3 class="text-sm font-bold uppercase tracking-wider">Entrada (Template SRE)</h3>
                </div>
                <div class="bg-primary/5 border border-primary/20 p-5 rounded-xl font-mono text-[10px] text-foreground leading-relaxed whitespace-pre-wrap break-words h-full min-h-[400px] overflow-x-hidden text-wrap">ALERTA DE SISTEMA - <span class="text-primary">{{ json(get(node-1)).id }}</span>

Servidor: <span class="text-primary">{{ json(get(node-1)).fonte }}</span> (Uptime: <span class="text-primary">{{ json(get(node-3))["servidor-web-01"].uptime_dias }}</span> dias)
Métrica: <span class="text-primary">{{ replace(json(get(node-1)).metrica, "_", " ") }}</span>
Valor: <span class="text-primary">{{ json(get(node-1)).valor_atual }}</span>% (Limite: <span class="text-primary">{{ json(get(node-1)).valor_limite }}</span>%)

SEVERIDADE: <span class="text-primary">{{ case(
  json(get(node-1)).valor_atual >= 95, "CRÍTICA",
  json(get(node-1)).valor_atual >= 85, "ALTA",
  "MÉDIA"
) }}</span>

Duração: <span class="text-primary">{{ if(json(get(node-1)).duracao_segundos < 60) { json(get(node-1)).duracao_segundos + "s" } else { json(get(node-1)).duracao_segundos / 60 + " min" } }}</span>

DISPONIBILIDADE: <span class="text-primary">{{ if(hora(agora()) >= 8 && hora(agora()) <= 18) { "Horário Comercial - Acionar " + at(json(get(node-4)), 0).nome } else { "Plantão Ativo" } }}</span>

RECOMENDAÇÃO: <span class="text-primary">{{ case(
  json(get(node-1)).valor_atual >= 85, "Investigação imediata e escalonamento para " + at(json(get(node-4)), 0).cargo,
  "Monitoramento preventivo nas próximas 2 horas"
) }}</span></div>
              </div>

              <!-- Saída -->
              <div>
                <div class="flex items-center gap-2 mb-3">
                  <div class="w-2 h-2 rounded-full bg-primary/60"></div>
                  <h3 class="text-sm font-bold uppercase tracking-wider text-primary-foreground/70">Saída (Notificação)</h3>
                </div>
                <div class="bg-primary/5 border border-primary/20 p-5 rounded-xl font-mono text-[10px] text-foreground leading-relaxed whitespace-pre-wrap h-full min-h-[400px]">ALERTA DE SISTEMA - ALT-2025-0124-001

Servidor: servidor-web-01 (Uptime: 45 dias)
Métrica: cpu usage
Valor: 89.5% (Limite: 80%)

SEVERIDADE: ALTA
Duração: 5 min
DISPONIBILIDADE: Horário Comercial - Acionar Ana Costa

RECOMENDAÇÃO: Investigação imediata e escalonamento para SRE Senior</div>
              </div>
            </div>

            <div>
              <h3 class="text-lg font-bold text-primary mb-2">5. Recursos Avançados</h3>
              <ul class="space-y-3">
                <li class="flex gap-2 text-sm">
                  <span class="font-bold text-primary min-w-[20px]">1.</span>
                  <span><strong>Acesso a Mapas Dinâmicos:</strong> Uso de chaves variables como <code>{{ json(get(node-3))["servidor-web-01"] }}</code> para acessar dados de dicionários.</span>
                </li>
                <li class="flex gap-2 text-sm">
                  <span class="font-bold text-primary min-w-[20px]">2.</span>
                  <span><strong>Manipulação de Strings:</strong> Função <code>replace()</code> para limpar nomes de métricas técnicas para leitura humana.</span>
                </li>
                <li class="flex gap-2 text-sm">
                  <span class="font-bold text-primary min-w-[20px]">3.</span>
                  <span><strong>Integração de Tempo:</strong> Uso de <code>hora(agora())</code> para tomar decisões baseadas no momento exato da execução.</span>
                </li>
              </ul>
            </div>
          </div>
        `
      }
    ],
  },
  "faq": {
    title: "FAQs e Melhores Práticas",
    description: "Encontre respostas para as dúvidas mais comuns e aprenda técnicas para otimizar seus fluxos.",
    sections: [
      {
        title: "Perguntas Frequentes",
        content: `
          <div class="space-y-6">
            <div class="p-6 rounded-xl border bg-card/50">
              <h4 class="font-bold text-primary mb-3 text-lg">Como verificar se uma variável existe antes de usá-la?</h4>
              <p class="text-sm mb-4">O AscFlow retorna uma string vazia ("") quando uma chave não existe. Para propriedades de objetos, verifique contra <code>undefined</code>.</p>
              <pre class="bg-muted p-4 rounded text-xs font-mono overflow-x-auto"><code>{{ if(get(node-1) != "") { "Autenticado" } else { "Acesso negado" } }}</code></pre>
            </div>

            <div class="p-6 rounded-xl border bg-card/50">
              <h4 class="font-bold text-primary mb-3 text-lg">Por que minha expressão matemática não está funcionando?</h4>
              <p class="text-sm mb-4">Dados de APIs chegam frequentemente como texto. Utilize <code>cast(valor, "number")</code> para converter propriedades e realizar cálculos precisos. Sem isso, o sistema pode tentar "concatenar" os textos (ex: "10" + "5" viraria "105") em vez de somar os números.</p>
              <pre class="bg-muted p-4 rounded text-xs font-mono overflow-x-auto leading-relaxed"><code>{{ 
  (cast(json(get(node-1)).preco, "number") * cast(json(get(node-1)).qtd, "number")) + 
  cast(json(get(node-2)).frete, "number") 
}}</code></pre>
            </div>

            <div class="p-6 rounded-xl border bg-card/50">
              <h4 class="font-bold text-primary mb-3 text-lg">Como formatar datas no padrão brasileiro?</h4>
              <p class="text-sm mb-4">Combine as funções de extração de componentes para criar o formato desejado:</p>
              <pre class="bg-muted p-4 rounded text-xs font-mono overflow-x-auto"><code>{{ day(agora()) }}/{{ month(agora()) }}/{{ year(agora()) }}</code></pre>
            </div>
          </div>
        `
      },
      {
        title: "Melhores Práticas para Desenvolvedores",
        content: `
          <div class="space-y-8">
            <!-- Estruturação -->
            <div class="bg-primary/5 p-6 rounded-xl border border-primary/20">
              <h4 class="font-bold text-primary mb-2 text-lg">Estruturação de Expressões</h4>
              <p class="text-sm text-muted-foreground mb-6">Mantenha expressões legíveis dividindo lógicas complexas em múltiplas linhas e usando indentação adequada.</p>
              
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div class="space-y-3">
                  <div class="text-[10px] font-bold uppercase text-primary/60 tracking-widest pl-1">Difícil de ler</div>
                  <pre class="bg-primary/5 p-4 rounded-lg text-[10px] leading-relaxed border border-primary/20 overflow-x-auto text-foreground"><code><span class="text-primary">{{ if(json(get(node-1)).score > 80 && json(get(node-2)).status == "ativo" && length(json(get(node-3)).historico) > 5) { "lead_prioritario" } else { "lead_comum" } }}</span></code></pre>
                </div>
                <div class="space-y-3">
                  <div class="text-[10px] font-bold uppercase text-primary tracking-widest pl-1">Mais legível</div>
                  <pre class="bg-primary/5 p-4 rounded-lg text-[10px] leading-relaxed border border-primary/20 overflow-x-auto text-foreground"><code><span class="text-primary">{{ if(
  json(get(node-1)).score > 80 && 
  json(get(node-2)).status == "ativo" &&
  length(json(get(node-3)).historico) > 5
) { 
  "lead_prioritario" 
} else { 
  "lead_comum" 
} }}</span></code></pre>
                </div>
              </div>
            </div>

            <!-- Nomenclatura -->
            <div class="bg-card p-6 rounded-xl border shadow-sm">
              <h4 class="font-bold text-primary mb-2 text-lg">Nomenclatura de Variáveis</h4>
              <p class="text-sm text-muted-foreground mb-4">Use nomes descritivos e consistentes para chaves de contexto, facilitando a manutenção e o entendimento do fluxo por outros desenvolvedores.</p>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="p-4 bg-muted/30 rounded-lg border border-dashed">
                  <span class="text-[10px] font-bold uppercase text-muted-foreground block mb-2">Evite nomes genéricos</span>
                  <div class="flex gap-2 flex-wrap">
                    <code class="text-xs bg-red-500/20 text-white px-2 py-1 rounded">data1</code>
                    <code class="text-xs bg-red-500/20 text-white px-2 py-1 rounded">info</code>
                    <code class="text-xs bg-red-500/20 text-white px-2 py-1 rounded">aux_var</code>
                  </div>
                </div>
                <div class="p-4 bg-muted/30 rounded-lg border border-dashed">
                  <span class="text-[10px] font-bold uppercase text-muted-foreground block mb-2">Use nomes descritivos</span>
                  <div class="flex gap-2 flex-wrap">
                    <code class="text-xs bg-green-500/20 text-white px-2 py-1 rounded">dados_cliente</code>
                    <code class="text-xs bg-green-500/20 text-white px-2 py-1 rounded">config_sistema</code>
                    <code class="text-xs bg-green-500/20 text-white px-2 py-1 rounded">resultado_api</code>
                  </div>
                </div>
              </div>
            </div>

            <!-- Validação e Segurança -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="bg-card p-6 rounded-xl border shadow-sm">
                <h4 class="font-bold text-primary mb-2 text-lg">Validação de Dados</h4>
                <p class="text-sm text-muted-foreground mb-4">Sempre valide dados antes de processá-los, especialmente quando vêm de fontes externas (APIs/Webhooks).</p>
                <div class="space-y-3">
                  <div class="text-[10px] font-bold text-muted-foreground uppercase">Exemplo: Validação de E-mail</div>
                  <pre class="bg-muted p-3 rounded text-[10px] font-mono overflow-x-auto leading-relaxed"><code>{{ 
  if(get(node-1) != "" && charindex("@", get(node-1)) > 0) { 
    "Email válido" 
  } else { 
    "Email ausente/inválido" 
  } 
}}</code></pre>
                </div>
              </div>
              <div class="bg-card p-6 rounded-xl border shadow-sm">
                <h4 class="font-bold text-primary mb-2 text-lg">Tratamento Gracioso</h4>
                <p class="text-sm text-muted-foreground mb-4">Implemente fallbacks para cenários onde dados dependentes podem estar ausentes no contexto.</p>
                <div class="space-y-3">
                  <div class="text-[10px] font-bold text-muted-foreground uppercase">Exemplo: Verificação de Propriedade</div>
                  <pre class="bg-muted p-3 rounded text-[10px] font-mono overflow-x-auto leading-relaxed"><code>{{ 
  json(get(node-1)).nome != undefined ? 
  json(get(node-1)).nome : 
  "Usuário Desconhecido" 
}}</code></pre>
                </div>
              </div>
            </div>

            <!-- Performance e Debug -->
            <div class="bg-card border rounded-xl overflow-hidden">
              <div class="bg-primary/5 p-6 border-b border-primary/10">
                <h4 class="font-bold text-primary mb-2 text-lg">Performance e Otimização</h4>
                <p class="text-sm text-muted-foreground">Evite chamadas repetidas a funções pesadas (como o parsing de grandes objetos JSON) dentro de loops ou condições complexas. Para segurança, <strong>nunca utilize eval()</strong> para processar entradas dinâmicas.</p>
              </div>
              <div class="p-6">
                 <div class="bg-primary/5 border border-primary/20 p-4 rounded-lg">
                  <h5 class="text-sm font-bold text-primary mb-1 italic">Dica de Manutenibilidade</h5>
                  <p class="text-sm text-foreground">Documente expressões complexas usando comentários XML-style no template: <code>&lt;!-- Calcula desconto progressivo --&gt; <span class="text-primary">{{ ... }}</span></code></p>
                </div>
              </div>
            </div>
          </div>
        `
      }
    ],
  },
  "referencia": {
    title: "Referência de Sintaxe",
    description: "Um guia rápido e completo de todas as funções, operadores e estruturas da linguagem AscFlow.",
    sections: [
      {
        title: "Tabela de Referência de Funções",
        content: `
          <div class="overflow-x-auto rounded-xl border border-border bg-card -mx-2 sm:mx-0">
            <table class="w-full text-left text-xs sm:text-sm border-collapse min-w-[500px]">
              <thead>
                <tr class="bg-muted/50 border-b">
                  <th class="p-2 sm:p-4 font-bold text-primary italic">Categoria</th>
                  <th class="p-2 sm:p-4 font-bold text-primary">Função</th>
                  <th class="p-2 sm:p-4 font-bold text-primary">Descrição</th>
                  <th class="p-2 sm:p-4 font-bold text-primary">Exemplo</th>
                </tr>
              </thead>
              <tbody class="divide-y">
                <tr>
                  <td class="p-2 sm:p-4 font-medium" rowspan="3">Acesso a Dados</td>
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">get(chave)</td>
                  <td class="p-2 sm:p-4">Busca valor no contexto</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ get("usuario_id") }}</td>
                </tr>
                <tr>
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">json(string)</td>
                  <td class="p-2 sm:p-4">Converte JSON string para objeto</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ json(get("dados")).nome }}</td>
                </tr>
                <tr>
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">tojson(valor)</td>
                  <td class="p-2 sm:p-4">Converte valor para JSON string</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ tojson(objeto) }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-medium" rowspan="6">Strings</td>
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">replace(str, busca, substitui)</td>
                  <td class="p-2 sm:p-4">Substitui texto</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ replace("João Silva", "Silva", "Santos") }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">split(str, delim)</td>
                  <td class="p-2 sm:p-4">Divide string em array</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ split("a,b,c", ",") }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">charindex(substr, str, [start])</td>
                  <td class="p-2 sm:p-4">Encontra posição de substring</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ charindex("@", email) }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">extractEmail(str)</td>
                  <td class="p-2 sm:p-4">Extrai email de texto</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ extractEmail("Contato: joao@email.com") }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">left(str, length)</td>
                  <td class="p-2 sm:p-4">Primeiros N caracteres</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ left("AscFlow", 3) }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">right(str, length)</td>
                  <td class="p-2 sm:p-4">Últimos N caracteres</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ right("AscFlow", 4) }}</td>
                </tr>
                <tr>
                  <td class="p-2 sm:p-4 font-medium">Conversão</td>
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">cast(valor, tipo)</td>
                  <td class="p-2 sm:p-4">Converte tipo</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ cast("123", "number") }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-medium" rowspan="9">Data/Tempo</td>
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">agora()</td>
                  <td class="p-2 sm:p-4">Data/hora atual</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ agora() }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">dateadd(data, valor, unidade)</td>
                  <td class="p-2 sm:p-4">Adiciona tempo</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ dateadd("2025-01-01", 7, "day") }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">datesub(data, valor, unidade)</td>
                  <td class="p-2 sm:p-4">Subtrai tempo</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ datesub(agora(), 1, "month") }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">hora(data)</td>
                  <td class="p-2 sm:p-4">Extrai hora</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ hora(agora()) }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">minute(data)</td>
                  <td class="p-2 sm:p-4">Extrai minuto</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ minute(agora()) }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">second(data)</td>
                  <td class="p-2 sm:p-4">Extrai segundo</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ second(agora()) }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">day(data)</td>
                  <td class="p-2 sm:p-4">Extrai dia</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ day(agora()) }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">month(data)</td>
                  <td class="p-2 sm:p-4">Extrai mês</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ month(agora()) }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">year(data)</td>
                  <td class="p-2 sm:p-4">Extrai ano</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ year(agora()) }}</td>
                </tr>
                <tr>
                  <td class="p-2 sm:p-4 font-medium" rowspan="3">Arrays</td>
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">at(array, indice)</td>
                  <td class="p-2 sm:p-4">Acessa elemento por índice</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ at(lista, 0) }}</td>
                </tr>
                <tr>
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">last(array)</td>
                  <td class="p-2 sm:p-4">Último elemento</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ last(lista) }}</td>
                </tr>
                <tr>
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">length(array)</td>
                  <td class="p-2 sm:p-4">Tamanho do array</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ length(lista) }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-medium" rowspan="2">Controle</td>
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">case(cond1, val1, ..., else)</td>
                  <td class="p-2 sm:p-4">Switch/case</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ case(status == "ok", "Sucesso", "Erro") }}</td>
                </tr>
                <tr class="bg-muted/5">
                  <td class="p-2 sm:p-4 font-mono text-primary text-xs">if(cond) { v } else { f }</td>
                  <td class="p-2 sm:p-4">Condicional</td>
                  <td class="p-2 sm:p-4 font-mono text-xs opacity-70">{{ if(idade >= 18) { "Adulto" } else { "Menor" } }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        `
      },
      {
        title: "Operadores e Controle",
        content: `
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="bg-card p-6 rounded-xl border border-border">
              <h4 class="font-bold text-primary mb-3">Operadores Suportados</h4>
              <ul class="space-y-2 text-sm">
                <li class="flex justify-between border-b pb-1"><span>Comparação</span> <span class="font-mono text-primary">== != &lt; &gt; &lt;= &gt;=</span></li>
                <li class="flex justify-between border-b pb-1"><span>Lógico</span> <span class="font-mono text-primary">&&</span></li>
                <li class="flex justify-between border-b pb-1"><span>Aritmético</span> <span class="font-mono text-primary">+ - * /</span></li>
                <li class="flex justify-between border-b pb-1"><span>Acesso</span> <span class="font-mono text-primary">. [ ]</span></li>
              </ul>
            </div>
            <div class="bg-card p-6 rounded-xl border border-border">
              <h4 class="font-bold text-primary mb-3">Sintaxe Condicional</h4>
              <pre class="bg-muted p-4 rounded text-xs font-mono"><code>{{ if(condicao) { 
  "verdadeiro" 
} else { 
  "falso" 
} }}</code></pre>
            </div>
          </div>
        `
      },
      {
        title: "Conclusão",
        content: `
          <div class="space-y-6">
            <p>A linguagem AscFlow representa uma solução poderosa e flexível para automação de fluxos de trabalho e processamento dinâmico de templates. Sua arquitetura baseada em interpretador permite criar soluções sofisticadas sem a complexidade de linguagens tradicionais.</p>
            
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div class="p-4 border rounded-lg bg-card">
                <h4 class="font-bold text-primary mb-1 text-sm">Simplicidade</h4>
                <p class="text-[10px] text-muted-foreground italic">Sintaxe declarativa e intuitiva para usuários com diferentes níveis técnicos.</p>
              </div>
              <div class="p-4 border rounded-lg bg-card">
                <h4 class="font-bold text-primary mb-1 text-sm">Flexibilidade</h4>
                <p class="text-[10px] text-muted-foreground italic">Sistema de contexto global que permite composição infinita de elementos.</p>
              </div>
              <div class="p-4 border rounded-lg bg-card">
                <h4 class="font-bold text-primary mb-1 text-sm">Performance</h4>
                <p class="text-[10px] text-muted-foreground italic">Processamento otimizado de templates para baixa latência em produção.</p>
              </div>
            </div>

            </div>
          </div>
        `
      }
    ],
  },
};

/* =====================================================
   COMPONENTE PRINCIPAL
   ===================================================== */
const Documentation = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(slug || "visao-geral");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    if (slug && slug !== activeSection) {
      setActiveSection(slug);
    }
  }, [slug]);

  const handleSectionChange = (id: string) => {
    setActiveSection(id);
    navigate(`/documentation/${id}`);
    setMobileSidebarOpen(false);
  };

  const [activeModule, setActiveModule] = useState(tutorialModules[0]);

  const sidebarItems = [
    {
      title: "Tutoriais em Vídeo",
      items: [
        { id: "tutoriais", label: "Módulos de Curso", icon: PlayCircle },
      ],
    },
    {
      title: "Começando",
      items: [
        { id: "visao-geral", label: "Visão Geral", icon: Book },
        { id: "quick-start", label: "Quick Start", icon: Zap },
        { id: "estrutura", label: "Estrutura de Flow", icon: GitBranch },
      ],
    },
    {
      title: "Conceitos Principais",
      items: [
        {
          id: "nodes",
          label: "Nodes",
          icon: Code,
          children: [
            { id: "nodes-concepts", label: "Conceitos e Uso (Guia)" },
            {
              id: "trigger",
              label: "Gatilhos (Triggers)",
              children: [
                { id: "node-schedule", label: "Schedule" },
                { id: "node-webhook", label: "Webhook" },
              ],
            },
            {
              id: "logic",
              label: "Lógica e Controle",
              children: [
                { id: "node-if", label: "IF / Condicional" },
                { id: "node-foreach", label: "ForEach (Loop)" },
                { id: "node-break", label: "Break (Parar Loop)" },
                { id: "node-sleep", label: "Sleep (Espera)" },
                { id: "node-execute-flow", label: "Executar Fluxo" },
                { id: "node-add-variable", label: "Add Variável" },
                { id: "node-object-transform", label: "Transformação de Objeto" },
                { id: "node-execute-process", label: "Executar Processo" },
              ],
            },
            {
              id: "integrations",
              label: "Integrações",
              children: [
                { id: "node-linkedin-users", label: "LinkedIn - Users Associados" },
                { id: "node-glpi-search", label: "GLPI - Buscar" },
                { id: "node-hubspot-contacts", label: "HubSpot - Contatos" },
                { id: "node-hubspot-companies", label: "HubSpot - Empresas" },
                { id: "node-hubspot-deals", label: "HubSpot - Negócios" },
                { id: "node-hubspot-engagements", label: "HubSpot - Compromissos" },
                { id: "node-hubspot-forms", label: "HubSpot - Formulários" },
                { id: "node-hubspot-tickets", label: "HubSpot - Tickets" },
                { id: "node-sharepoint", label: "Microsoft SharePoint" },
                { id: "node-google-drive", label: "Google Drive - Operações" },
                { id: "node-whatsapp-api", label: "Whatsapp API" },
              ],
            },
            {
              id: "azure-devops",
              label: "Azure DevOps",
              children: [
                { id: "node-ado-users", label: "Buscar Usuários" },
                { id: "node-ado-user", label: "Buscar Usuário" },
                { id: "node-ado-workitems", label: "Buscar Workitems (WIQL)" },
                { id: "node-ado-workitem", label: "Buscar Workitem (ID)" },
                { id: "node-ado-workitems-recursive", label: "Buscar Workitems (Recorrente)" },
                { id: "node-ado-workitems-history", label: "Buscar Histórico WorkItems" },
              ],
            },
            {
              id: "visualization",
              label: "Visualização de Dados",
              children: [
                { id: "node-dashboard-generator", label: "Dashboard Generator" },
                { id: "node-powerbi-refresh", label: "Power BI - Atualizar Conjunto" },
                { id: "node-powerbi-print", label: "Power BI - Print" },
              ],
            },
            {
              id: "db-cloud",
              label: "Banco de Dados & Cloud",
              children: [
                { id: "node-azure-storage", label: "Azure Storage" },
                { id: "node-postgresql", label: "PostgreSQL" },
                { id: "node-mssql", label: "MS SQL Server" },
                { id: "node-mssql-job", label: "MS SQL Server - Job" },
                { id: "node-odbc-query", label: "Exec. Query SQL (ODBC)" },
              ],
            },
            {
              id: "ai",
              label: "Inteligência Artificial (AI)",
              children: [
                { id: "node-openai-chat", label: "OpenAI - Texto (Chat)" },
                { id: "node-openai-image", label: "OpenAI - Imagem" },
                { id: "node-openai-audio", label: "OpenAI - Áudio" },
                { id: "node-openai-file", label: "OpenAI - Arquivo" },
                { id: "node-openai-function", label: "OpenAI - Function Calling" },
                { id: "node-openai-structured-output", label: "OpenAI - Saída Estruturada" },
                { id: "node-openai-embeddings", label: "OpenAI - Embeddings" },
                { id: "node-openai-web-search", label: "OpenAI - Web Search" },
                { id: "node-gemini-chat", label: "Google Gemini" },
                { id: "node-smart-agent-call", label: "Smart Agent Hub - Call" },
                { id: "node-smart-agent-chat", label: "Smart Agent Hub - Agente" },
              ],
            },
            {
              id: "utils",
              label: "Utilitários (Helpers)",
              children: [
                { id: "node-email-send", label: "Enviar Email" },
                { id: "node-google-search", label: "WebSearch Google" },
                { id: "node-web-request", label: "Web Request (HTTP)" },
                { id: "node-web-data-extractor", label: "Web Data Extractor" },
                { id: "node-webhook-response", label: "Resposta Webhook" },
                { id: "node-html-writer", label: "HTML Writer" },
                { id: "node-html-to-pdf", label: "HTML -> PDF" },
                { id: "node-converter", label: "Converter (Multiformato)" },
              ],
            },
          ],
        },
        { id: "variaveis", label: "Variáveis e Contextos", icon: Settings },
        { id: "condicoes", label: "Condições e Expressões", icon: Shield },
        { id: "interpolacao", label: "Interpolação e Formatação", icon: Braces },
        { id: "funcoes", label: "Funções Embutidas", icon: FunctionSquare },
        { id: "validacoes", label: "Validações", icon: CheckCircle },
        { id: "exemplos", label: "Exemplos Completos", icon: FileText },
        { id: "faq", label: "FAQs e Melhores Práticas", icon: CheckCircle },
        { id: "referencia", label: "Referência de Sintaxe", icon: BookOpen },
      ],
    },
  ];

  // Fallback para quando a seção não existe
  const section = documentationData[activeSection] || documentationData["visao-geral"];

  // Sidebar content reusable for both desktop and mobile
  const renderSidebarContent = () => (
    <>
      {sidebarItems.map((group) => (
        <div key={group.title} className="mb-6">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-2">
            {group.title}
          </h3>
          <ul className="space-y-1">
            {group.items.map((item: any) => {
              if (item.children) {
                return (
                  <li key={item.id}>
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value={item.id} className="border-none">
                        <AccordionTrigger className="py-2 px-2 hover:bg-muted rounded-md hover:no-underline font-normal">
                          <div className="flex items-center">
                            {item.icon && <item.icon className="h-4 w-4 mr-2" />}
                            <span>{item.label}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-0 pl-4">
                          {item.children.map((child: any) => (
                            <div key={child.id}>
                              {child.children ? (
                                <Accordion type="single" collapsible className="w-full">
                                  <AccordionItem value={child.id} className="border-none">
                                    <AccordionTrigger className="py-2 px-2 hover:bg-muted rounded-md hover:no-underline font-normal text-sm text-left [&>svg]:ml-auto">
                                      {child.label}
                                    </AccordionTrigger>
                                    <AccordionContent className="pb-0 pl-4">
                                      {child.children.map((subChild: any) => (
                                        <button
                                          key={subChild.id}
                                          onClick={() => handleSectionChange(subChild.id)}
                                          className={`flex items-center w-full px-2 py-1.5 text-left rounded-md transition text-sm ${activeSection === subChild.id
                                            ? "bg-primary/10 text-primary"
                                            : "hover:bg-muted"
                                            }`}
                                        >
                                          {subChild.label}
                                        </button>
                                      ))}
                                    </AccordionContent>
                                  </AccordionItem>
                                </Accordion>
                              ) : (
                                <button
                                  onClick={() => handleSectionChange(child.id)}
                                  className={`flex items-center w-full px-2 py-1.5 text-left rounded-md transition text-sm ${activeSection === child.id
                                    ? "bg-primary/10 text-primary"
                                    : "hover:bg-muted"
                                    }`}
                                >
                                  {child.label}
                                </button>
                              )}
                            </div>
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </li>
                );
              }
              return (
                <li key={item.id}>
                  <button
                    onClick={() => handleSectionChange(item.id)}
                    className={`flex items-center w-full px-2 py-1.5 text-left rounded-md transition ${activeSection === item.id
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted"
                      }`}
                  >
                    {item.icon && <item.icon className="h-4 w-4 mr-2" />}
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Mobile menu button */}
      <button
        onClick={() => setMobileSidebarOpen(true)}
        className="md:hidden fixed top-28 left-4 z-40 bg-primary text-primary-foreground p-2 rounded-lg shadow-lg hover:bg-primary/90 transition-colors"
        aria-label="Abrir menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Mobile sidebar overlay */}
      {mobileSidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar drawer */}
      <aside
        className={`md:hidden fixed top-0 left-0 h-full w-72 bg-background border-r border-border p-4 pt-6 z-50 overflow-y-auto transition-transform duration-300 ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-primary">Documentação</h2>
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="p-1 rounded-md hover:bg-muted transition-colors"
            aria-label="Fechar menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {renderSidebarContent()}
      </aside>

      <div className="flex flex-1 mt-24">
        {/* Desktop sidebar */}
        <aside className="w-64 flex-shrink-0 border-r border-border bg-muted/30 p-4 fixed top-24 h-[calc(100vh-6rem)] overflow-y-auto hidden md:block">
          {renderSidebarContent()}
        </aside>

        {/* Main content */}
        <div className="flex flex-col flex-1 md:ml-64 min-h-[calc(100vh-6rem)] justify-between">
          <main className="overflow-y-auto p-4 sm:p-6 md:p-8 flex-1">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-2xl sm:text-3xl font-bold mb-4">{section.title}</h1>
              <p className="text-sm sm:text-base text-muted-foreground mb-6 whitespace-pre-line">
                {section.description}
              </p>

              {activeSection === "tutoriais" ? (
                /* ── PLAYER DE TUTORIAIS ── */
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Lista de módulos */}
                  <aside className="lg:w-64 shrink-0">
                    <div className="rounded-xl border border-border bg-card/60 overflow-hidden">
                      <div className="px-4 py-3 border-b border-border">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Conteúdo</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {tutorialModules.filter((m) => !m.isLocked).length} de {tutorialModules.length} disponíveis
                        </p>
                      </div>
                      <ul className="divide-y divide-border">
                        {tutorialModules.map((mod) => {
                          const isActive = activeModule.id === mod.id;
                          return (
                            <li key={mod.id}>
                              <button
                                onClick={() => !mod.isLocked && setActiveModule(mod)}
                                disabled={mod.isLocked}
                                className={`w-full text-left px-4 py-3 flex items-start gap-2 transition-all duration-200
                                  ${mod.isLocked ? "opacity-40 cursor-not-allowed" : "hover:bg-[#7C3AED10] cursor-pointer"}
                                  ${isActive ? "bg-[#7C3AED15] border-l-2 border-l-[#7C3AED]" : ""}
                                `}
                              >
                                <span className="mt-0.5 shrink-0">
                                  {mod.isLocked
                                    ? <Lock size={14} className="text-muted-foreground" />
                                    : <PlayCircle size={14} className={isActive ? "text-[#7C3AED]" : "text-[#1B75D0]"} />
                                  }
                                </span>
                                <span className={`text-xs leading-snug ${isActive ? "text-[#7C3AED] font-medium" : "text-foreground"}`}>
                                  <span className="text-muted-foreground mr-1">{String(mod.id).padStart(2,"0")} —</span>
                                  {mod.title}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </aside>

                  {/* Player + descrição */}
                  <div className="flex-1 min-w-0">
                    <div className="rounded-xl overflow-hidden border border-border bg-black aspect-video shadow-[0_0_30px_rgba(124,58,237,0.12)]">
                      {activeModule.videoSrc ? (
                        <video
                          key={activeModule.videoSrc}
                          src={activeModule.videoSrc}
                          controls
                          controlsList="nodownload"
                          className="w-full h-full"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#0d0d1a] to-[#1a0a2e]">
                          <PlayCircle size={48} className="text-[#7C3AED40] mb-3" />
                          <p className="text-muted-foreground text-sm">Vídeo em breve</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-4 rounded-xl border border-border bg-card/60 p-4">
                      <p className="text-xs text-[#7C3AED] font-semibold uppercase tracking-widest mb-1">
                        Módulo {String(activeModule.id).padStart(2,"0")}
                      </p>
                      <h2 className="text-lg font-bold text-foreground mb-2">{activeModule.title}</h2>
                      <p className="text-sm text-muted-foreground">{activeModule.description}</p>
                    </div>
                  </div>
                </div>
              ) : (
                section.sections.map((sub, i) => (
                  <Fragment key={i}>
                    {sub.title && <h2 className="text-lg sm:text-xl font-semibold mt-6 mb-2">{sub.title}</h2>}
                    <div
                      className="leading-relaxed text-foreground whitespace-pre-line text-sm sm:text-base overflow-x-hidden"
                      dangerouslySetInnerHTML={{ __html: sub.content }}
                    />
                  </Fragment>
                ))
              )}
            </div>
          </main>

          <Footer />
        </div>
      </div>
    </div>
  );
};

export default Documentation;